"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import type { Locale } from "./templates/ServicePageTemplate";
import { isHrefHidden } from "@/lib/booking/hidden-services";

const footerLinksSv = [
  {
    title: "Ungdomskraft",
    links: [
      { label: "Om oss", href: "/om-oss" },
      { label: "Recensioner", href: "https://se.trustpilot.com/review/ungdomskraft.com" },
      { label: "Jobba hos oss", href: "/jobba-hos-oss" },
      { label: "Kontakt", href: "/kontakta-oss" },
      { label: "Guide", href: "/guide" },
    ],
  },
  {
    title: "Tjänster",
    links: [
      { label: "Bortforsling", href: "/bortforsling" },
      { label: "Tömningar", href: "/tomma-kallare" },
      { label: "Bärhjälp", href: "/barhjalp" },
      { label: "Flytthjälp", href: "/flytthjalp" },
      { label: "Transport förråd", href: "/transport-forrad" },
      { label: "Transport möbler", href: "/transport-mobler" },
      { label: "Transport vitvaror", href: "/transport-vitvaror" },
      { label: "Transporthjälp", href: "/transporthjalp" },
      { label: "Transport återbruk", href: "/transport-aterbruk" },
      { label: "Hemleverans butik", href: "/hemleverans-butik" },
      { label: "Hemleverans auktion", href: "/hemleverans-auktion" },
      { label: "Bud", href: "/bud" },
      { label: "Möbelmontering", href: "/mobelmontering" },
    ],
  },
];

const footerLinksEn = [
  {
    title: "Ungdomskraft",
    links: [
      { label: "About us", href: "/en/about-us" },
      { label: "Reviews", href: "https://se.trustpilot.com/review/ungdomskraft.com" },
      { label: "Work with us", href: "/en/work-with-us" },
      { label: "Contact", href: "/en/contact-us" },
      { label: "Guide", href: "/en/guide" },
    ],
  },
  {
    title: "Services",
    links: [
      { label: "Junk Removal", href: "/en/junk-removal" },
      { label: "Property Clearance", href: "/en/basement-clearance" },
      { label: "Carrying Help", href: "/en/carrying-help" },
      { label: "Moving Help", href: "/en/moving-help" },
      { label: "Storage Transport", href: "/en/storage-transport" },
      { label: "Furniture Transport", href: "/en/furniture-transport" },
      { label: "Appliance Transport", href: "/en/appliance-transport" },
      { label: "Transport Help", href: "/en/transport-help" },
      { label: "Recycling Transport", href: "/en/recycling-transport" },
      { label: "Store Delivery", href: "/en/store-delivery" },
      { label: "Auction Delivery", href: "/en/auction-delivery" },
      { label: "Courier Service", href: "/en/courier-service" },
      { label: "Furniture Assembly", href: "/en/furniture-assembly" },
    ],
  },
];

const footerT = {
  sv: { tagline: "Hjälp med vad som helst, var som helst, när som helst!", bookNow: "Boka nu", terms: "Användarvillkor", privacy: "Integritetspolicy", deleteAccount: "Radera konto (app)", credit: "Hemsida av" },
  en: { tagline: "Help with anything, anywhere, anytime!", bookNow: "Book now", terms: "Terms of service", privacy: "Privacy policy", deleteAccount: "Delete account (app)", credit: "Website by" },
};

const socialLinks = [
  {
    label: "Instagram",
    href: "https://instagram.com/ungdomskraft.co",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="h-[18px] w-[18px]">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    ),
  },
  {
    label: "Facebook",
    href: "https://facebook.com/ungdomskraft",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="h-[18px] w-[18px]">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
  {
    label: "TikTok",
    href: "https://tiktok.com/@ungdomskraft",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="h-[18px] w-[18px]">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
      </svg>
    ),
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/ungdomskraft-ab/",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="h-[18px] w-[18px]">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
];

export default function Footer({
  locale = "sv",
  bookingHref,
}: {
  locale?: Locale;
  bookingHref?: string;
}) {
  const footerLinks = locale === "en" ? footerLinksEn : footerLinksSv;
  const l = footerT[locale];
  const href = bookingHref ?? (locale === "en" ? "/en/book" : "/boka");
  const trustboxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const w = window as unknown as {
      Trustpilot?: { loadFromElement: (el: HTMLElement, flag: boolean) => void };
    };
    if (w.Trustpilot && trustboxRef.current) {
      w.Trustpilot.loadFromElement(trustboxRef.current, true);
    }
  }, []);

  return (
    <footer className="bg-white border-t border-gray-200">
      <div className="mx-auto max-w-screen-2xl px-6 xl:px-14">
        {/* Main footer content */}
        <div className="grid grid-cols-2 md:grid-cols-[repeat(4,1fr)_auto_auto] gap-x-8 gap-y-10 py-12 lg:py-16">
          {/* Link columns */}
          {footerLinks.map((col) => {
            const isServices = col.links.length > 5;
            if (isServices) {
              const third = Math.ceil(col.links.length / 3);
              const chunks = [
                col.links.slice(0, third),
                col.links.slice(third, third * 2),
                col.links.slice(third * 2),
              ];
              return chunks.map((chunk, i) => (
                <div key={`${col.title}-${i}`}>
                  {i === 0 && (
                    <h3 className="text-sm font-semibold text-brand-dark mb-4">
                      {col.title}
                    </h3>
                  )}
                  <ul className={`space-y-2.5 ${i > 0 ? "md:mt-8" : ""}`}>
                    {chunk.map((link) => (
                      <li key={link.label}>
                        <a
                          href={link.href}
                          {...(link.href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                          className="text-sm text-brand-primary hover:text-brand-secondary transition-colors whitespace-nowrap"
                        >
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ));
            }
            return (
              <div key={col.title}>
                <h3 className="text-sm font-semibold text-brand-dark mb-4">
                  {col.title}
                </h3>
                <ul className="space-y-2.5">
                  {col.links.filter((link) => !isHrefHidden(link.href)).map((link) => (
                    <li key={link.label}>
                      <a
                        href={link.href}
                        {...(link.href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                        className="text-sm text-brand-primary hover:text-brand-secondary transition-colors whitespace-nowrap"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          {/* Trustpilot + Gasell */}
          <div className="col-span-2 md:col-span-1 flex flex-col items-center md:items-start">
            <div
              ref={trustboxRef}
              className="trustpilot-widget scale-[0.85] origin-center md:origin-left"
              data-locale="sv-SE"
              data-template-id="53aa8807dec7e10d38f59f32"
              data-businessunit-id="60a37dbef975970001397303"
              data-style-height="130px"
              data-style-width="220px"
              data-token="d7411ab0-350c-4c4b-87f7-13e43c491167"
              data-theme="light"
            >
              <a
                href="https://se.trustpilot.com/review/ungdomskraft.com"
                target="_blank"
                rel="noopener"
              >
                Trustpilot
              </a>
            </div>
            <div className="grid grid-cols-4 md:grid-cols-2 items-center gap-3 sm:gap-4 mt-4">
              <Image src="/gasell-1.avif" alt="DI Gasell 2023" width={160} height={104} className="h-8 sm:h-10 w-auto object-contain" />
              <Image src="/gasell-2.avif" alt="DI Gasell 2024" width={160} height={104} className="h-8 sm:h-10 w-auto object-contain" />
              <Image src="/gasell-3.avif" alt="DI Gasell 2025" width={160} height={104} className="h-8 sm:h-10 w-auto object-contain" />
              <Image src="/gasell-4.avif" alt="DI Gasell 2026" width={160} height={104} className="h-8 sm:h-10 w-auto object-contain" />
            </div>
          </div>

          {/* CTA column */}
          <div className="col-span-2 md:col-span-1 flex flex-col items-center md:items-start gap-4">
            <p className="text-sm text-gray-600 leading-relaxed max-w-[220px] text-center md:text-left">
              {l.tagline}
            </p>
            <a
              href={href}
              className="w-full md:max-w-[220px] flex items-center justify-center gap-2 rounded-xl bg-brand-secondary px-8 py-3.5 text-sm font-semibold text-white hover:brightness-110 hover:scale-[1.02] transition-all duration-200 shadow-md"
            >
              {l.bookNow}
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-6 border-t border-gray-200">
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            <span>Ungdomskraft &copy; {new Date().getFullYear()}</span>
            <span>Södra Hamnvägen 44, 115 41 Stockholm</span>
            <Link href={locale === "en" ? "/en/terms-of-service" : "/allmanna-villkor"} className="hover:text-brand-dark transition-colors">
              {l.terms}
            </Link>
            <Link href="/integritetspolicy" className="hover:text-brand-dark transition-colors">
              {l.privacy}
            </Link>
            <Link href={locale === "en" ? "/en/delete-account" : "/radera-konto"} className="hover:text-brand-dark transition-colors">
              {l.deleteAccount}
            </Link>
            {/* Byråns signatur, som på wedrive.se: text + Koda-loggan, en länk. */}
            <a
              href="https://www.koda.se"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${l.credit} Koda`}
              className="inline-flex items-center gap-2 transition-opacity hover:opacity-70"
            >
              {l.credit}
              <Image
                src="/images/koda-logo-dark.png"
                alt="Koda"
                width={640}
                height={151}
                sizes="72px"
                className="h-[14px] w-auto opacity-70"
              />
            </a>
          </div>

          <div className="flex items-center gap-3">
            {socialLinks.map(({ label, href, icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="text-gray-400 hover:text-brand-secondary transition-colors duration-200"
              >
                {icon}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
